/**
 * src/auth/authFetch.ts
 *
 * Attache automatiquement `Authorization: Bearer <token de session>` à toute requête même-origine
 * vers /api/* ou /ts/* — plutôt que de threader un header dans chaque appel fetch() (le code a de
 * nombreux points d'appel indépendants : src/api/http.ts, deepkiApi.ts/energyApi.ts (leur propre
 * client), AIAnalysisButton.tsx, ReportCreator.tsx, MeterConsoModal.tsx, dataChecker.ts via
 * energyCache.ts, etc. — risque réel d'en oublier un et d'obtenir des 401 silencieux), on wrap
 * `window.fetch` une seule fois au bootstrap (cf. installAuthFetch() appelée dans main.tsx).
 *
 * Le token vient de getSessionToken() (cf. msalClient.ts) — synchrone, lu depuis sessionStorage,
 * aucun appel MSAL ici.
 *
 * Sur un 401 (token de session expiré, ~1h — cf. docs/authentification.md), un unique
 * rafraîchissement silencieux (ssoSilent, jamais de popup ici : ce fetch peut être déclenché hors de
 * tout geste utilisateur) est tenté via trySilentReauth() (msalClient.ts — déduplique les requêtes
 * concurrentes), puis la requête est rejouée une fois avec le nouveau token. Si le rafraîchissement
 * échoue, notifySessionInvalid() prévient AuthContext.tsx (bandeau "reconnexion nécessaire",
 * cf. App.tsx) et le 401 d'origine est retourné tel quel — chaque call site garde son propre
 * traitement d'erreur existant (ex. ReportA4.tsx::saveToDb() retente alors une reconnexion
 * interactive, cette fois avec popup autorisé puisqu'elle découle directement du clic "Sauvegarder").
 */

import { getSessionToken, trySilentReauth, notifySessionInvalid } from "./msalClient";

let installed = false;

export function installAuthFetch() {
  if (installed) return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  /** Attache le Bearer courant (relu à chaque appel — peut avoir changé entre deux tentatives) et fetch. */
  function attempt(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const token = getSessionToken();
    if (!token) return originalFetch(input, init);

    if (input instanceof Request) {
      const headers = new Headers(input.headers);
      headers.set("Authorization", `Bearer ${token}`);
      return originalFetch(new Request(input, { headers }), init);
    }

    const headers = new Headers(init?.headers);
    headers.set("Authorization", `Bearer ${token}`);
    return originalFetch(input, { ...init, headers });
  }

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
    if (!isAuthenticatedPath(url)) return originalFetch(input, init);

    const res = await attempt(input, init);
    if (res.status !== 401 || isLoginRoute(url)) return res;

    // input instanceof Request : body potentiellement un flux à usage unique déjà consommé par la
    // 1ère tentative — jamais rejoué (aucun call site de l'app ne passe un Request à fetch() en
    // pratique, cf. docstring de fichier, mais on reste défensif).
    if (input instanceof Request) { notifySessionInvalid(); return res; }

    const refreshed = await trySilentReauth();
    if (refreshed) return attempt(input, init);

    notifySessionInvalid();
    return res;
  };
}

/** Même-origine uniquement — jamais de token envoyé vers Microsoft Graph/SharePoint/Anthropic. */
function isAuthenticatedPath(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin !== window.location.origin) return false;
    return parsed.pathname.startsWith("/api/") || parsed.pathname.startsWith("/ts/");
  } catch {
    return false;
  }
}

/**
 * POST /auth/login (sous apiBaseUrl, "/api" par défaut — cf. app.config.ts) : jamais de
 * retry-reauth dessus, c'est justement la route qui délivre le token — la rejouer sur son propre
 * échec bouclerait. Suffixe plutôt que chemin exact pour rester valide même si apiBaseUrl est
 * personnalisé via VITE_API_BASE_URL.
 */
function isLoginRoute(url: string): boolean {
  try { return new URL(url, window.location.origin).pathname.endsWith("/auth/login"); }
  catch { return false; }
}
